/**
 * Kopírovať / Vystrihnúť / Vložiť riadky tabuľky dielcov (KOLO 181).
 *
 * User: *„to pravé tlačidlo — tam daj hore ikonky zbaliť/rozbaliť
 * a kopírovať, vložiť a vystrihnúť"*. Schránka si pamätá **id riadkov**,
 * nie ich obsah — kopíruje sa až pri Vložiť, na serveri
 * (`POST /zostavy/{id}/vlozit-dielce`), takže kópia nesie všetko vrátane
 * vrstiev sendviča, vnorených operácií a pridaných hrán.
 *
 * - **Kópia** sa dá vložiť kamkoľvek a opakovane — aj v inej zákazke
 *   (slot sa priradí podľa materiálu dosky, chýbajúci pribudne).
 * - **Vystrihnuté** sa dá vložiť len v tej istej variante a raz; po
 *   vložení sa schránka vyprázdni ako vo Windows.
 *
 * Stav stránky ako schránka hrán (`prenosHran.ts`) — prežije prechod
 * medzi zákazkami, nie obnovenie stránky. Systémovú schránku nepoužíva:
 * id riadku mimo appky nič neznamená a Ctrl+C na označenom texte ostáva
 * kopírovaním textu.
 */
import { useSyncExternalStore } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, extractErrorMessage } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import type { DataResponse, Dielec } from "@/api/schemas";
import type { components } from "@/api/types.gen";
import { toast } from "@/components/ui/use-toast";
import { sklonuj } from "@/lib/format";
import { useUndoOptional } from "@/lib/undo/UndoContext";

export type RezimSchranky = "kopia" | "presun";

type VlozitDielceResponse = components["schemas"]["VlozitDielceResponse"];

export interface RiadokSchranky {
  id: string;
  kod: string | null;
}

export interface SchrankaRiadkov {
  rezim: RezimSchranky;
  zakazkaId: string;
  /** Číslo zákazky do hlášky „Vložené 3 dielce z T.00124". */
  zakazkaCislo: string | null;
  variantaId: string;
  dielce: RiadokSchranky[];
}

let schranka: SchrankaRiadkov | null = null;
const posluchaci = new Set<() => void>();

export function nastavSchrankuRiadkov(nova: SchrankaRiadkov | null): void {
  schranka = nova;
  for (const f of posluchaci) f();
}

function prihlas(f: () => void): () => void {
  posluchaci.add(f);
  return () => posluchaci.delete(f);
}

export function useSchrankaRiadkov(): SchrankaRiadkov | null {
  return useSyncExternalStore(prihlas, () => schranka);
}

export type MoznostVlozenia = { ok: true } | { ok: false; dovod: string };

/** Dá sa schránka vložiť do tejto varianty? Dôvod ide do `title`. */
export function mozeVlozit(
  s: SchrankaRiadkov | null,
  variantaId: string,
): MoznostVlozenia {
  if (!s || s.dielce.length === 0) {
    return { ok: false, dovod: "Schránka je prázdna — najprv riadok skopíruj alebo vystrihni" };
  }
  if (s.rezim === "presun" && s.variantaId !== variantaId) {
    return {
      ok: false,
      dovod: "Vystrihnuté dielce vložíš len v tej istej variante — do inej ich skopíruj",
    };
  }
  return { ok: true };
}

/** Id vystrihnutých riadkov — tabuľka ich stlmí, kým sa nevložia. */
export function vystrihnuteIds(s: SchrankaRiadkov | null): ReadonlySet<string> {
  return new Set(s?.rezim === "presun" ? s.dielce.map((d) => d.id) : []);
}

export function popisPoctu(n: number): string {
  return `${n} ${sklonuj(n, ["dielec", "dielce", "dielcov"])}`;
}

export interface VlozenieRiadkov {
  zostavaId: string;
  dielce: string[];
  zaDielecId: string | null;
  rezim: RezimSchranky;
  /** Text kroku Vrátiť („vloženie 3 dielcov", „duplikovanie dielca A2"). */
  popis: string;
}

/**
 * Akcie schránky pre editor jednej varianty — tabuľka (menu riadku)
 * aj skratky Ctrl+C / Ctrl+X / Ctrl+V idú cez tento hook.
 */
export function useAkcieSchranky({
  zakazkaId,
  zakazkaCislo,
  variantaId,
}: {
  zakazkaId: string;
  zakazkaCislo: string | null;
  variantaId: string;
}) {
  const aktualna = useSchrankaRiadkov();
  const qc = useQueryClient();
  const undo = useUndoOptional();

  async function obnov(): Promise<void> {
    await Promise.all([
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) }),
      qc.invalidateQueries({ queryKey: queryKeys.varianty.edgesSummaries() }),
      qc.invalidateQueries({ queryKey: queryKeys.varianty.kovaniaSummaries() }),
    ]);
  }

  const vlozenie = useMutation({
    mutationFn: async (v: VlozenieRiadkov) => {
      const { data } = await api.post<DataResponse<VlozitDielceResponse>>(
        `/zostavy/${v.zostavaId}/vlozit-dielce`,
        { dielce: v.dielce, za_dielec_id: v.zaDielecId, rezim: v.rezim },
      );
      return data.data;
    },
    onSuccess: (vysledok, v) => {
      // Jeden krok Vrátiť za celé vloženie — kópia sa zmaže (deti padnú
      // s rodičom), presun sa vráti na pôvodné miesta
      const predtym = vysledok.predtym;
      undo?.pridaj({
        popis:
          (vysledok.pridane_sloty ?? []).length > 0
            ? `${v.popis} (nový materiál ostane)`
            : v.popis,
        vrat: async () => {
          if (v.rezim === "presun" && predtym) {
            await api.post(`/varianty/${variantaId}/vrat-presun-dielcov`, {
              dielce: predtym,
            });
          } else {
            for (const id of vysledok.vlozene) await api.delete(`/dielce/${id}`);
          }
          await obnov();
        },
      });
      void obnov();
    },
  });

  function zapamataj(rezim: RezimSchranky, riadky: Dielec[]): void {
    if (riadky.length === 0) return;
    nastavSchrankuRiadkov({
      rezim,
      zakazkaId,
      zakazkaCislo,
      variantaId,
      dielce: riadky.map((d) => ({ id: d.id, kod: d.kod ?? null })),
    });
    toast({
      title: `${rezim === "kopia" ? "Skopírované" : "Vystrihnuté"}: ${popisPoctu(riadky.length)}`,
      description: "Vlož pravým tlačidlom → Vložiť, alebo Ctrl+V.",
    });
  }

  /** Vloženie bez schránky — Duplikovať je kópia za ten istý riadok. */
  async function vlozRiadky(v: VlozenieRiadkov): Promise<VlozitDielceResponse | null> {
    try {
      return await vlozenie.mutateAsync(v);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Vložiť sa nepodarilo",
        description: extractErrorMessage(err),
      });
      return null;
    }
  }

  /** Vloží schránku za riadok `zaDielecId` (`null` = na koniec zostavy). */
  async function vloz(zostavaId: string, zaDielecId: string | null): Promise<void> {
    const s = aktualna;
    const moznost = mozeVlozit(s, variantaId);
    if (!s || !moznost.ok) {
      if (!moznost.ok) toast({ title: "Nedá sa vložiť", description: moznost.dovod });
      return;
    }
    const pocet = popisPoctu(s.dielce.length);
    const vysledok = await vlozRiadky({
      zostavaId,
      dielce: s.dielce.map((d) => d.id),
      zaDielecId,
      rezim: s.rezim,
      popis: s.rezim === "presun" ? `presun ${pocet}` : `vloženie ${pocet}`,
    });
    if (!vysledok) return;
    if (s.rezim === "presun") nastavSchrankuRiadkov(null);
    const odinakial =
      s.zakazkaId !== zakazkaId && s.zakazkaCislo ? ` z ${s.zakazkaCislo}` : "";
    const sloty = (vysledok.pridane_sloty ?? [])
      .map((p) => `${p.slot_kod} ${p.material_kod ?? p.material_nazov}`)
      .join(", ");
    toast({
      title: `${s.rezim === "presun" ? "Presunuté" : "Vložené"}: ${pocet}${odinakial}`,
      description: sloty
        ? `Pribudol materiál ${sloty} — cieľ ho nemal.`
        : undefined,
    });
  }

  return {
    schranka: aktualna,
    moznost: mozeVlozit(aktualna, variantaId),
    vystrihnute: vystrihnuteIds(aktualna),
    kopiruj: (riadky: Dielec[]) => zapamataj("kopia", riadky),
    vystrihni: (riadky: Dielec[]) => zapamataj("presun", riadky),
    zrusVystrihnutie: () => {
      if (aktualna?.rezim === "presun") nastavSchrankuRiadkov(null);
    },
    vloz,
    vlozRiadky,
    bezi: vlozenie.isPending,
  };
}

export type AkcieSchranky = ReturnType<typeof useAkcieSchranky>;
